import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

type TeamMember = Tables<"team_members">;
type TeamMemberInsert = InsertTables<"team_members">;
type TeamMemberUpdate = UpdateTables<"team_members">;

export type TeamMemberWithUser = TeamMember & {
  user: Tables<"users">;
};

async function fetchTeamMembers(teamId: string): Promise<TeamMemberWithUser[]> {
  const { data, error } = await supabase
    .from("team_members")
    .select("*, user:users(*)")
    .eq("team_id", teamId)
    .order("created_at");

  if (error) throw error;
  return (data as TeamMemberWithUser[]) || [];
}

async function addTeamMember(
  teamMember: TeamMemberInsert,
): Promise<TeamMember> {
  const { data, error } = await supabase
    .from("team_members")
    .insert(teamMember)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateTeamMember(
  id: string,
  teamMember: TeamMemberUpdate,
): Promise<TeamMember> {
  const { data, error } = await supabase
    .from("team_members")
    .update(teamMember)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function removeTeamMember(id: string): Promise<void> {
  const { error } = await supabase.from("team_members").delete().eq("id", id);

  if (error) throw error;
}

export function useTeamMembers(teamId: string | undefined) {
  return useQuery({
    queryKey: ["team-members", teamId],
    queryFn: () => (teamId ? fetchTeamMembers(teamId) : []),
    enabled: !!teamId,
  });
}

export function useAddTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addTeamMember,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["team-members", data.team_id],
      });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["teams", data.team_id] });
    },
  });
}

export function useUpdateTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      teamMember,
    }: {
      id: string;
      teamMember: TeamMemberUpdate;
    }) => updateTeamMember(id, teamMember),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["team-members", data.team_id],
      });
      queryClient.invalidateQueries({ queryKey: ["teams", data.team_id] });
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; teamId: string }) =>
      removeTeamMember(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["team-members", variables.teamId],
      });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({
        queryKey: ["teams", variables.teamId],
      });
    },
  });
}
